import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import api from '../../services/api';
import { useAuth } from '../auth';
import { useToast } from '../toast';

import {
  IOnboardingStep,
  IOnboardingContextData,
  TutorialId,
  ITutorialProgress,
} from './types';
import { tutorialStepsMap } from './tutorialSteps';

// Re-exportar tipos públicos
export { TutorialId, OnboardingStepType } from './types';
export type {
  IOnboardingStep as OnboardingStep,
  IOnboardingContextData as OnboardingContextData,
} from './types';

const OnboardingContext = createContext<IOnboardingContextData>(
  {} as IOnboardingContextData,
);

const CACHE_PREFIX = 'fuzzymakers_tutorial_';
const getCacheKey = (id: TutorialId): string => `${CACHE_PREFIX}${id}`;

export const OnboardingProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { user, updateUser } = useAuth();
  const { addToast } = useToast();

  // Estado principal
  const [isOnboardingActive, setIsOnboardingActive] = useState(false);
  const [isSpotlightActive, setIsSpotlightActive] = useState(false);
  const [activeTutorialId, setActiveTutorialId] = useState<TutorialId | null>(
    null,
  );
  const [steps, setSteps] = useState<IOnboardingStep[]>([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [completedActions, setCompletedActions] = useState<
    Record<string, string[]>
  >({});
  const [isLoading, setIsLoading] = useState(false);

  // ============================================================
  // Cache helpers
  // ============================================================
  const loadCachedProgress = useCallback(
    (tutorialId: TutorialId): ITutorialProgress | null => {
      const cached = localStorage.getItem(getCacheKey(tutorialId));
      if (!cached) return null;

      try {
        const progress: ITutorialProgress = JSON.parse(cached);
        const now = Date.now();
        // Cache válido por 7 dias
        if (now - progress.lastUpdated < 7 * 24 * 60 * 60 * 1000) {
          return progress;
        }
        localStorage.removeItem(getCacheKey(tutorialId));
      } catch {
        localStorage.removeItem(getCacheKey(tutorialId));
      }
      return null;
    },
    [],
  );

  const saveCachedProgress = useCallback(
    (
      tutorialId: TutorialId,
      stepIndex: number,
      currentSteps: IOnboardingStep[],
      actions: Record<string, string[]>,
    ) => {
      const progress: ITutorialProgress = {
        currentStepIndex: stepIndex,
        completedSteps: currentSteps.filter(s => s.completed).map(s => s.id),
        completedActions: actions,
        lastUpdated: Date.now(),
      };
      localStorage.setItem(getCacheKey(tutorialId), JSON.stringify(progress));
    },
    [],
  );

  const clearCache = useCallback((tutorialId: TutorialId) => {
    localStorage.removeItem(getCacheKey(tutorialId));
  }, []);

  // ============================================================
  // Persistir progresso quando mudar
  // ============================================================
  useEffect(() => {
    if (!activeTutorialId || steps.length === 0 || !isOnboardingActive) return;
    saveCachedProgress(
      activeTutorialId,
      currentStepIndex,
      steps,
      completedActions,
    );
  }, [
    steps,
    currentStepIndex,
    completedActions,
    activeTutorialId,
    isOnboardingActive,
    saveCachedProgress,
  ]);

  // ============================================================
  // Dados derivados
  // ============================================================
  const currentStep = useMemo(
    () => steps[currentStepIndex] || null,
    [steps, currentStepIndex],
  );

  const targetSelector = useMemo(
    () => currentStep?.targetSelector || null,
    [currentStep],
  );

  const tooltipPosition = useMemo(
    () => currentStep?.position || 'bottom',
    [currentStep],
  );

  // ============================================================
  // Iniciar tutorial (genérico)
  // ============================================================
  const startTutorial = useCallback(
    (tutorialId: TutorialId): void => {
      // Onboarding só funciona se o usuário está logado
      if (!user) return;

      const baseSteps = tutorialStepsMap[tutorialId];
      if (!baseSteps || baseSteps.length === 0) return;

      // Clonar steps para não mutar os originais
      let tutorialSteps = baseSteps.map(s => ({ ...s }));
      let startIndex = 0;

      // Restaurar progresso do cache se existir
      const cached = loadCachedProgress(tutorialId);
      if (cached) {
        startIndex = cached.currentStepIndex || 0;
        tutorialSteps = tutorialSteps.map(step => ({
          ...step,
          completed: cached.completedSteps?.includes(step.id) || false,
        }));
        setCompletedActions(cached.completedActions || {});
      } else {
        setCompletedActions({});
      }

      setActiveTutorialId(tutorialId);
      setSteps(tutorialSteps);
      setCurrentStepIndex(startIndex);
      setIsOnboardingActive(true);
      setIsSpotlightActive(true);
      setIsLoading(false);
    },
    [user, loadCachedProgress],
  );

  // Atalho para o tutorial inicial
  const startOnboarding = useCallback(
    () => startTutorial(TutorialId.INITIAL),
    [startTutorial],
  );

  // ============================================================
  // Fechar / Pular
  // ============================================================
  const deactivate = useCallback(() => {
    setIsOnboardingActive(false);
    setIsSpotlightActive(false);
  }, []);

  const closeModal = useCallback(() => {
    deactivate();
  }, [deactivate]);

  const skipTutorial = useCallback(async () => {
    deactivate();

    addToast({
      type: 'info',
      expression: 'Ok!',
      description:
        'Você pode retomar o tutorial a qualquer momento pelo botão de ajuda.',
    });

    // Se for o tutorial inicial, salvar preferência no backend
    if (activeTutorialId === TutorialId.INITIAL) {
      try {
        await api.post('/users/onboarding/skip');

        if (user) {
          updateUser({
            ...user,
            onboarding_skipped: true,
          });
        }
      } catch (error) {
        // Falha silenciosa — cache mantém o progresso
      }
    }
  }, [activeTutorialId, deactivate, addToast, user, updateUser]);

  // Alias backward compat
  const skipOnboarding = skipTutorial;

  // ============================================================
  // Navegação entre steps
  // ============================================================
  const nextStep = useCallback(() => {
    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex(prev => prev + 1);
    }
  }, [currentStepIndex, steps.length]);

  const previousStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
    }
  }, [currentStepIndex]);

  const toggleSpotlight = useCallback(() => {
    setIsSpotlightActive(prev => !prev);
  }, []);

  // ============================================================
  // Completar step
  // ============================================================
  const completeStep = useCallback(
    (stepId: string) => {
      const stepToComplete = steps.find(step => step.id === stepId);
      if (!stepToComplete) return;

      // Verificar ações requeridas
      if (stepToComplete.requiredActions?.length) {
        const validActions = stepToComplete.requiredActions.filter(
          a => a.trim() !== '',
        );

        if (validActions.length > 0) {
          const done = completedActions[stepId] || [];
          const allDone = validActions.every(a => done.includes(a));

          if (!allDone) {
            return;
          }
        }
      }

      // Marcar como completado
      setSteps(prev =>
        prev.map(step =>
          step.id === stepId ? { ...step, completed: true } : step,
        ),
      );

      // Avançar automaticamente
      if (currentStepIndex < steps.length - 1) {
        nextStep();
      }
    },
    [steps, completedActions, currentStepIndex, nextStep],
  );

  // ============================================================
  // Ações requeridas
  // ============================================================
  const markActionCompleted = useCallback(
    (stepId: string, actionId: string) => {
      setCompletedActions(prev => {
        const stepActions = prev[stepId] || [];
        if (stepActions.includes(actionId)) return prev;
        return {
          ...prev,
          [stepId]: [...stepActions, actionId],
        };
      });
    },
    [],
  );

  // Auto-completar quando todas as ações estiverem prontas
  useEffect(() => {
    if (!currentStep) return;

    const stepId = currentStep.id;
    const stepObj = steps.find(s => s.id === stepId);
    if (!stepObj || stepObj.completed) return;

    const required = (stepObj.requiredActions || []).filter(
      a => a.trim() !== '',
    );
    if (required.length === 0) return;

    const done = completedActions[stepId] || [];
    if (required.every(a => done.includes(a))) {
      completeStep(stepId);
    }
  }, [completeStep, completedActions, currentStep, steps]);

  const isStepCompleted = useCallback(
    (stepId: string) =>
      steps.find(step => step.id === stepId)?.completed || false,
    [steps],
  );

  // ============================================================
  // Completar tutorial
  // ============================================================
  const completeTutorial = useCallback(async () => {
    // Capturar referências antes de desativar
    const tutorialId = activeTutorialId;
    const completedSteps = steps.filter(s => s.completed).map(s => s.id);

    // Fechar o tutorial imediatamente (antes de qualquer requisição)
    deactivate();

    if (tutorialId) {
      clearCache(tutorialId);
    }

    addToast({
      type: 'success',
      expression: 'Parabéns!',
      description: 'Tutorial concluído com sucesso!',
    });

    // Enviar requisição ao backend em segundo plano (apenas tutorial inicial)
    if (tutorialId === TutorialId.INITIAL) {
      try {
        await api.post('/users/onboarding/complete', {
          steps: completedSteps,
        });

        if (user) {
          updateUser({
            ...user,
            onboarding_completed: true,
            onboarding_completed_at: new Date().toISOString(),
          });
        }
      } catch {
        // Falha silenciosa — o tutorial já foi fechado para o usuário
      }
    }
  }, [
    activeTutorialId,
    steps,
    user,
    updateUser,
    deactivate,
    clearCache,
    addToast,
  ]);

  // Alias backward compat
  const completeOnboarding = completeTutorial;

  // ============================================================
  // Reset
  // ============================================================
  const resetTutorial = useCallback(() => {
    setCurrentStepIndex(0);
    setCompletedActions({});
    setSteps(prev => prev.map(step => ({ ...step, completed: false })));

    if (activeTutorialId) {
      clearCache(activeTutorialId);
    }
  }, [activeTutorialId, clearCache]);

  const resetOnboarding = resetTutorial;

  const markCurrentStepActionCompleted = useCallback(
    (actionId: string) => {
      if (isOnboardingActive && currentStep && actionId) {
        markActionCompleted(currentStep.id, actionId);
      }
    },
    [currentStep, isOnboardingActive, markActionCompleted],
  );

  // ============================================================
  // Provider
  // ============================================================
  return (
    <OnboardingContext.Provider
      value={{
        isOnboardingActive,
        isSpotlightActive,
        currentStep,
        steps,
        targetSelector,
        tooltipPosition,
        completedActions,
        isLoading,
        activeTutorialId,
        // Ações
        startTutorial,
        startOnboarding,
        skipTutorial,
        skipOnboarding,
        nextStep,
        previousStep,
        completeStep,
        completeTutorial,
        completeOnboarding,
        markActionCompleted,
        markCurrentStepActionCompleted,
        isStepCompleted,
        resetTutorial,
        resetOnboarding,
        closeModal,
        toggleSpotlight,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};

export function useOnboarding(): IOnboardingContextData {
  const context = useContext(OnboardingContext);

  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }

  return context;
}
